import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { KeyRound, Loader2, Eye, EyeOff, CheckCircle, XCircle } from 'lucide-react';
import { toast } from '../lib/safeToast';

const MIN_PASSWORD_LENGTH = 8;

function PasswordRequirement({ met, text }) {
  return (
    <div className={`flex items-center gap-1.5 text-xs ${met ? 'text-emerald-600' : 'text-slate-400'}`}>
      {met ? <CheckCircle className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />}
      {text}
    </div>
  );
}

export default function ForcePasswordChangeModal() {
  const { user, api, refreshUser } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  if (!user?.must_change_password) return null;

  const hasMinLength = newPassword.length >= MIN_PASSWORD_LENGTH;
  const hasUppercase = /[A-Z]/.test(newPassword);
  const hasLowercase = /[a-z]/.test(newPassword);
  const hasNumber = /[0-9]/.test(newPassword);
  const hasSpecial = /[^A-Za-z0-9]/.test(newPassword);
  const passwordsMatch = newPassword && confirmPassword && newPassword === confirmPassword;
  const notSameAsCurrent = newPassword && currentPassword && newPassword !== currentPassword;
  const allValid = hasMinLength && hasUppercase && hasLowercase && hasNumber && passwordsMatch && notSameAsCurrent;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!allValid) {
      toast.error('Please meet all password requirements');
      return;
    }
    setLoading(true);
    try {
      await api.put('/settings/password', {
        current_password: currentPassword,
        new_password: newPassword,
      });
      toast.success('Password changed successfully. Welcome to PayZen!');
      await refreshUser();
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to change password';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={() => {}}>
      <DialogContent
        className="sm:max-w-md"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#0F172A]">
            <KeyRound className="w-5 h-5 text-[#0B6445]" />
            Change Your Password
          </DialogTitle>
          <DialogDescription className="text-slate-600">
            You are using a temporary password. For security, you must set a new password before continuing.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-2">
          <div>
            <Label htmlFor="force-current-pw" className="text-sm font-medium text-slate-700">
              Current Password
            </Label>
            <div className="relative mt-1">
              <Input
                id="force-current-pw"
                type={showCurrent ? 'text' : 'password'}
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Enter your temporary password"
                required
                autoComplete="current-password"
                className="pr-10"
              />
              <button
                type="button"
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                onClick={() => setShowCurrent(!showCurrent)}
                tabIndex={-1}
              >
                {showCurrent ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <div>
            <Label htmlFor="force-new-pw" className="text-sm font-medium text-slate-700">
              New Password
            </Label>
            <div className="relative mt-1">
              <Input
                id="force-new-pw"
                type={showNew ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Choose a strong password"
                required
                autoComplete="new-password"
                className="pr-10"
                minLength={MIN_PASSWORD_LENGTH}
              />
              <button
                type="button"
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                onClick={() => setShowNew(!showNew)}
                tabIndex={-1}
              >
                {showNew ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <div>
            <Label htmlFor="force-confirm-pw" className="text-sm font-medium text-slate-700">
              Confirm New Password
            </Label>
            <div className="relative mt-1">
              <Input
                id="force-confirm-pw"
                type={showConfirm ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Re-enter your new password"
                required
                autoComplete="new-password"
                className="pr-10"
              />
              <button
                type="button"
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                onClick={() => setShowConfirm(!showConfirm)}
                tabIndex={-1}
              >
                {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {newPassword && (
            <div className="rounded-lg border border-[#DCE4E0] bg-[#F8FAFC] p-3 space-y-1.5">
              <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1">Password Requirements</p>
              <PasswordRequirement met={hasMinLength} text={`At least ${MIN_PASSWORD_LENGTH} characters`} />
              <PasswordRequirement met={hasUppercase} text="At least one uppercase letter" />
              <PasswordRequirement met={hasLowercase} text="At least one lowercase letter" />
              <PasswordRequirement met={hasNumber} text="At least one number" />
              <PasswordRequirement met={hasSpecial} text="At least one special character (recommended)" />
              <PasswordRequirement met={notSameAsCurrent} text="Different from current password" />
              <PasswordRequirement met={passwordsMatch} text="Passwords match" />
            </div>
          )}

          <DialogFooter className="pt-2">
            <Button
              type="submit"
              disabled={loading || !allValid}
              className="w-full bg-[#0B6445] hover:bg-[#095b3d] text-white"
            >
              {loading ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Changing Password…</>
              ) : (
                <><KeyRound className="w-4 h-4 mr-2" />Set New Password</>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
